import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createTeacherStore,
  deleteTeacherStore,
  fetchStoreAccess,
  fetchStudentStore,
  fetchStudentTeachers,
  deleteStoreItem,
  fetchStoreItems,
  fetchStorePurchases,
  fetchTeacherStore,
  createStoreItem,
  purchaseStoreItem,
  setGroupStoreAccess,
  updateStoreItem,
  updateStorePurchaseStatus,
  setStudentStoreAccess,
} from "../services/api";

export interface StoreAccess {
  students: { studentId: number; visible: boolean }[];
  groups: { groupId: number; visible: boolean }[];
}

export function useTeacherStore(userId?: number | null, enabled = true) {
  return useQuery<{ store: { user_id: number; created_at: string } | null }>({
    queryKey: ["teacherStore", userId],
    queryFn: fetchTeacherStore,
    enabled: enabled && !!userId,
  });
}

export function useStoreAccess(userId?: number | null, enabled = true) {
  return useQuery<StoreAccess>({
    queryKey: ["storeAccess", userId],
    queryFn: fetchStoreAccess,
    enabled: enabled && !!userId,
  });
}

export interface StudentTeacher {
  teacherId: number;
  name: string;
  surname: string;
  avatar: string | null;
}

export interface StudentStoreItem {
  itemId: number;
  title: string;
  description: string | null;
  price: number;
  category: string | null;
  inStock: boolean;
  stockQuantity: number | null;
  itemImage: string | null;
}

export interface StudentStorePurchase {
  purchaseId: number;
  itemTitle: string;
  price: number;
  status: "pending" | "fulfilled" | "rejected";
  createdAt: string;
}

export interface StudentStore {
  balance: number;
  items: StudentStoreItem[];
  purchases: StudentStorePurchase[];
}

export function useStudentTeachers() {
  return useQuery<StudentTeacher[]>({
    queryKey: ["studentTeachers"],
    queryFn: fetchStudentTeachers,
  });
}

export function useStudentStore(teacherId?: number, enabled = true) {
  return useQuery<StudentStore>({
    queryKey: ["studentStore", teacherId],
    queryFn: () => fetchStudentStore(teacherId as number),
    enabled: enabled && !!teacherId,
    retry: false,
  });
}

export function useCreateTeacherStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTeacherStore,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["teacherStore"] }),
  });
}

export function useDeleteTeacherStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTeacherStore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacherStore"] });
      queryClient.invalidateQueries({ queryKey: ["storeAccess"] });
    },
  });
}

export interface TeacherStoreItem {
  itemId: number;
  title: string;
  description: string;
  price: number;
  category: string | null;
  stockQuantity: number | null;
  itemImage: string | null;
  isActive: boolean;
}

export interface TeacherStorePurchase {
  purchaseId: number;
  studentId: number;
  studentName: string;
  studentSurname: string;
  itemTitle: string;
  price: number;
  status: "pending" | "fulfilled" | "rejected";
  createdAt: string;
}

export function useStoreItems(userId?: number | null, enabled = true) {
  return useQuery<TeacherStoreItem[]>({
    queryKey: ["storeItems", userId],
    queryFn: fetchStoreItems,
    enabled: enabled && !!userId,
  });
}

export function useStorePurchases(userId?: number | null, enabled = true) {
  return useQuery<TeacherStorePurchase[]>({
    queryKey: ["storePurchases", userId],
    queryFn: fetchStorePurchases,
    enabled: enabled && !!userId,
  });
}

export function useSaveStoreItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, data }: { itemId?: number; data: Record<string, unknown> }) =>
      itemId ? updateStoreItem(itemId, data) : createStoreItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storeItems"] });
      queryClient.invalidateQueries({ queryKey: ["studentStore"] });
    },
  });
}

export function useDeleteStoreItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteStoreItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storeItems"] });
      queryClient.invalidateQueries({ queryKey: ["studentStore"] });
    },
  });
}

export function useUpdateStorePurchaseStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ purchaseId, status }: { purchaseId: number; status: "fulfilled" | "rejected" }) =>
      updateStorePurchaseStatus(purchaseId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storePurchases"] });
      queryClient.invalidateQueries({ queryKey: ["studentStore"] });
    },
  });
}

export function usePurchaseStoreItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ teacherId, itemId }: { teacherId: number; itemId: number }) =>
      purchaseStoreItem(teacherId, itemId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["studentStore"] }),
  });
}

export function useSetStudentStoreAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ studentId, visible }: { studentId: number; visible: boolean }) =>
      setStudentStoreAccess(studentId, visible),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["storeAccess"] }),
  });
}

export function useSetGroupStoreAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, visible }: { groupId: number; visible: boolean }) =>
      setGroupStoreAccess(groupId, visible),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["storeAccess"] }),
  });
}
